import { and, asc, desc, eq, getTableColumns, ilike, inArray, isNotNull, isNull, ne, or, sql } from "drizzle-orm";

import type { WsAccount } from "@storyboard/web-client";
import type { UserInfoResponse } from "@storyboard/web-client/auth";
import { createLogger } from "@/logger";

const log = createLogger("query");

import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { executeBuiltQuery, Tx } from "@/db/drizzleUtils";
import {
  account,
  feed,
  feedGroup,
  feedGroupMembership,
  feedPreference,
  item,
  permission,
  transcription,
  workspaceCommandAlias,
  workspaceMembership,
  wsMembershipAvailability,
} from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";
import { sqlMembershipIsOffline, sqlMembershipIsOnline } from "@/models/repositories/membershipOfflineSql";
import type { DriverSortKey, DriverViewTab } from "@/types/driver";

export type { UserInfoResponse };

export async function upsertMyAccount(userInfo: UserInfoResponse, tx?: Tx) {
  const accountId = userInfo?.session?.credentials?.[0].CredentialScopes?.[0].accountId;

  if (!accountId) {
    log.error("No account id found in userInfo", userInfo);
    return;
  }

  const data = {
    id: accountId,
    mine: true,
    preferredLanguage: null as string | null,
    name: userInfo?.name,
    accountType: userInfo?.accountType,
    avatarColor: userInfo?.avatarColor,
    email: userInfo?.email,
    firstName: null as string | null,
    lastName: null as string | null,
    phoneNumber: null as string | null,
    emailVerified: false,
    phoneNumberVerified: false,
    bot: userInfo?.name === "Storyboard",
  };

  const results = await executeBuiltQuery(
    db
      .insert(account)
      .values(data)
      .onConflictDoUpdate({
        target: account.id,
        set: {
          mine: true,
          name: userInfo?.name,
          accountType: userInfo?.accountType,
          avatarColor: userInfo?.avatarColor,
          email: userInfo?.email,
        },
      })
      .returning({
        ...getTableColumns(account),
      }),
    "upsertMyAccount",
    tx,
  );

  emitTableUpdates(["account"]);
  return results;
}

export async function bulkUpsertAccount(records: WsAccount[]) {
  if (records.length === 0) return;
  const data = records.map((accountRecord) => ({
    id: accountRecord.id,
    name: accountRecord.name || null,
    accountType: accountRecord.accountType || null,
    avatarColor: accountRecord.avatarColor || null,
    firstName: accountRecord.firstName || null,
    lastName: accountRecord.lastName || null,
    email: accountRecord.email || null,
    emailVerified: accountRecord.emailVerified,
    phoneNumber: accountRecord.phoneNumber || null,
    phoneNumberVerified: accountRecord.phoneNumberVerified,
    bot: accountRecord?.name === "Storyboard",
  }));
  const deduped = deduplicateByKeyValue(data, (item) => item.id);
  await bulkInsert(account, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [account.id],
      set: {
        name: sql`"excluded"."name"`,
        accountType: sql`"excluded"."accountType"`,
        avatarColor: sql`"excluded"."avatarColor"`,
        firstName: sql`"excluded"."firstName"`,
        lastName: sql`"excluded"."lastName"`,
        email: sql`"excluded"."email"`,
        emailVerified: sql`"excluded"."emailVerified"`,
        phoneNumber: sql`"excluded"."phoneNumber"`,
        phoneNumberVerified: sql`"excluded"."phoneNumberVerified"`,
        bot: sql`"excluded"."bot"`,
      },
    },
  });
  emitTableUpdates(["account"]);
}

export async function setPreferredLanguage(accountId: string, language: string): Promise<void> {
  await db.update(account).set({ preferredLanguage: language }).where(eq(account.id, accountId)).execute();
  emitTableUpdates(["account"]);
}

export const accountsWithNoAvailabilities = (workspaceId: string) => {
  const query = db
    .select({
      feedId: workspaceCommandAlias.feedId,
      workspaceId: workspaceMembership.workspaceId,
      membershipStatus: sql<"active" | "inactive">`${workspaceMembership.status} as "membershipStatus"`,
      contact: sql<string>`COALESCE(${account.name}, ${account.email}, ${account.phoneNumber}) as contact`,
      role: sql<"limitedMember">`${workspaceMembership.role} as "role"`,
      date: sql<string>`${workspaceCommandAlias.createdAt} as date`,
      title: feed.title,
      isAliasFeed: feed.isAliasFeed,
      aliasMembershipId: feed.aliasMembershipId,
      accountId: sql<string>`${account.id} as "accountId"`,
    })
    .from(account)
    .innerJoin(workspaceMembership, eq(workspaceMembership.accountId, account.id))
    .leftJoin(wsMembershipAvailability, eq(wsMembershipAvailability.workspaceMembershipId, workspaceMembership.id))
    .innerJoin(workspaceCommandAlias, eq(workspaceCommandAlias.workspaceMembershipId, workspaceMembership.id))
    .innerJoin(feed, eq(feed.id, workspaceCommandAlias.feedId))
    .where(
      and(
        isNull(wsMembershipAvailability.workspaceMembershipId),
        eq(workspaceMembership.workspaceId, workspaceId),
        eq(workspaceMembership.status, "active"),
        eq(workspaceMembership.role, "limitedMember"),
      ),
    );

  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Accounts With No Availabilities", query);
  return query;
};

export const lookupAccountById = async (accountId: string): Promise<WsAccount | null> => {
  if (!accountId) {
    return null;
  }
  const response = await db
    .select(getTableColumns(account))
    .from(account)
    .where(eq(account.id, accountId))
    .limit(1)
    .execute();
  if (response?.length === 1) {
    return response[0];
  }
  return null;
};

export const accountsWhereIHaveWritePermission = ({
  workspaceId,
  membershipId,
  myCurrentWorkspaceMembershipId,
  type,
  role,
  groupFilterId,
  channelSort,
  statusFilter,
  searchQuery,
  language = "en",
}: {
  workspaceId: string;
  membershipId: string;
  myCurrentWorkspaceMembershipId: string;
  type: DriverViewTab;
  role: string;
  groupFilterId: null | string[];
  channelSort: DriverSortKey;
  statusFilter?: string[];
  searchQuery?: string;
  language?: string;
}) => {
  const latestItem = db
    .selectDistinctOn([item.feedId], {
      feedId: item.feedId,
      contentId: item.contentId,
      createdAt: item.createdAt,
    })
    .from(item)
    .where(isNull(item.deletedAt))
    .orderBy(item.feedId, desc(item.createdAt))
    .as("latestItem");

  const hasUnread = db
    .select({
      feedId: item.feedId,
      hasUnread: sql<boolean>`bool_or(${item.unread})`.as("hasUnread"),
    })
    .from(item)
    .where(isNull(item.deletedAt))
    .groupBy(item.feedId)
    .as("hasUnread");

  const latestItemAlias = db
    .selectDistinctOn([workspaceCommandAlias.feedId], {
      feedId: workspaceCommandAlias.feedId,
      alias: workspaceCommandAlias.alias,
    })
    .from(workspaceCommandAlias)
    .orderBy(workspaceCommandAlias.feedId)
    .as("latestItemAlias");

  const orderByClause =
    channelSort === "alphaAsc"
      ? [asc(account.name), account.id]
      : channelSort === "alphaDesc"
        ? [desc(account.name), account.id]
        : channelSort === "most-recent"
          ? [desc(latestItem.createdAt), account.id]
          : [desc(hasUnread.hasUnread), desc(latestItem.createdAt), account.id];

  const distinctOn =
    channelSort === "alphaAsc" || channelSort === "alphaDesc"
      ? [account.name, account.id]
      : channelSort === "most-recent"
        ? [latestItem.createdAt, account.id]
        : [hasUnread.hasUnread, latestItem.createdAt, account.id];

  const query = db
    .selectDistinctOn(distinctOn, {
      accountId: account.id,
      driver: account.name,
      workspaceId: feed.workspaceId,
      feedId: feed.id,
      recentMessage: transcription.transcriptionContent,
      alias: latestItemAlias.alias,
      unread: hasUnread.hasUnread,
      status: wsMembershipAvailability.mostRecentDutyStatusStatus,
      location: sql<
        string | null
      >`NULLIF(CONCAT_WS(', ', NULLIF(${wsMembershipAvailability.mostRecentDeviceLocationNearestAddressMunicipality}, ''), NULLIF(${wsMembershipAvailability.mostRecentDeviceLocationNearestAddressRegion}, '')), '')`.as(
        "location",
      ),
      volume: wsMembershipAvailability.mostRecentVolumeVolume,
      country: wsMembershipAvailability.mostRecentDeviceLocationNearestAddressCountry,
      stateProvince: wsMembershipAvailability.mostRecentDeviceLocationNearestAddressRegion,
      feedPrefId: feedPreference.id,
      group: feedGroup.name,
      groupId: feedGroup.id,
      calculatedAvailability: wsMembershipAvailability.calculatedAvailability,
      offline: wsMembershipAvailability.offline,
      mostRecentHandsFreeStatusEnabled: wsMembershipAvailability.mostRecentHandsFreeStatusEnabled,
      mostRecentHandsFreeStatusUpdatedAt: wsMembershipAvailability.mostRecentHandsFreeStatusUpdatedAt,
      mostRecentVolumeAudible: wsMembershipAvailability.mostRecentVolumeAudible,
      mostRecentDrivingStatus: wsMembershipAvailability.mostRecentDrivingStatus,
      mostRecentDutyStatusStatus: wsMembershipAvailability.mostRecentDutyStatusStatus,
      mostRecentDutyStatusChangedAt: wsMembershipAvailability.mostRecentDutyStatusChangedAt,
      mostRecentDrivingStatusChangedAt: wsMembershipAvailability.mostRecentDrivingStatusChangedAt,
    })
    .from(account)
    .innerJoin(workspaceMembership, eq(workspaceMembership.accountId, account.id))
    .innerJoin(feed, eq(feed.aliasMembershipId, workspaceMembership.id))
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feed.id),
        eq(permission.workspaceMembershipId, membershipId),
        eq(permission.name, "write"),
        eq(permission.enabled, true),
      ),
    )
    .leftJoin(latestItemAlias, eq(latestItemAlias.feedId, feed.id))
    .leftJoin(latestItem, eq(latestItem.feedId, feed.id))
    .leftJoin(hasUnread, eq(hasUnread.feedId, feed.id))
    .leftJoin(
      transcription,
      and(eq(transcription.contentId, latestItem.contentId), eq(transcription.language, language)),
    )
    .leftJoin(wsMembershipAvailability, eq(wsMembershipAvailability.workspaceMembershipId, workspaceMembership.id))
    .leftJoin(feedGroupMembership, eq(feedGroupMembership.feedId, feed.id))
    .leftJoin(feedGroup, eq(feedGroup.id, feedGroupMembership.groupId))
    .leftJoin(
      feedPreference,
      and(
        eq(feedPreference.feedId, feed.id),
        eq(feedPreference.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        ne(feedPreference.pushNotificationPreference, "none"),
      ),
    )
    .where(
      and(
        eq(latestItemAlias.alias, role),
        eq(workspaceMembership.workspaceId, workspaceId),
        eq(workspaceMembership.status, "active"),
        eq(feed.isAliasFeed, true),
        searchQuery?.length > 0
          ? or(
              ilike(feed.title, `%${searchQuery.replace(/[\\%_]/g, "\\$&")}%`),
              ilike(feedGroup.name, `%${searchQuery.replace(/[\\%_]/g, "\\$&")}%`),
              ilike(account.name, `%${searchQuery.replace(/[\\%_]/g, "\\$&")}%`),
            )
          : undefined,
        groupFilterId?.length > 0 ? inArray(feedGroup.id, groupFilterId) : undefined,
        type === "my" ? isNotNull(feedPreference.id) : undefined,
        statusFilter && statusFilter.length > 0
          ? or(
              ...statusFilter.map((s) =>
                s === "driving"
                  ? and(eq(wsMembershipAvailability.calculatedAvailability, "present"), sqlMembershipIsOnline())
                  : s === "not-driving"
                    ? and(
                        ne(wsMembershipAvailability.calculatedAvailability, "present"),
                        sqlMembershipIsOnline(),
                        or(
                          eq(wsMembershipAvailability.calculatedAvailability, "unknown"),
                          and(
                            eq(wsMembershipAvailability.calculatedAvailability, "notPresent"),
                            or(
                              ne(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled, false),
                              isNull(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled),
                            ),
                            sql`NOT (
                              ${wsMembershipAvailability.mostRecentHandsFreeStatusEnabled} IS TRUE
                              AND ${wsMembershipAvailability.mostRecentVolumeAudible} IS FALSE
                            )`,
                          ),
                        ),
                      )
                    : s === "hands-free-off"
                      ? and(
                          eq(wsMembershipAvailability.calculatedAvailability, "notPresent"),
                          sqlMembershipIsOnline(),
                          eq(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled, false),
                        )
                      : s === "volume-muted"
                        ? and(
                            eq(wsMembershipAvailability.calculatedAvailability, "notPresent"),
                            sqlMembershipIsOnline(),
                            eq(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled, true),
                            eq(wsMembershipAvailability.mostRecentVolumeAudible, false),
                          )
                        : s === "offline"
                          ? sqlMembershipIsOffline()
                          : s === "never-logged-in"
                            ? isNull(wsMembershipAvailability.workspaceMembershipId) // this is incorrect, the data needs to come from Pending Invites
                            : undefined,
              ),
            )
          : undefined,
      ),
    )
    .orderBy(...orderByClause);

  if (!workspaceId || !membershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Accounts Where I Have Write Permission", query);
  return query;
};
